import { BrandclubTermsAndConditions } from "@/components/pages/Auth/TermsAndConditions";
import { useAsyncCheckoutEnabled } from "../../../../hooks/useAsyncCheckoutEnabled";
import { CartTotal } from "../../../components/CartTotal";
import { SectionTitle } from "../../../components/CollapsibleSection";
import DebugPanel from "../../../components/DebugPanel";
import { RetailerErrorAndTerms } from "../../../components/ErrorAndTerms";
import { MobileOrderSummaryProduct } from "./MobileOrderSummaryProduct";
import { Box, Skeleton, styled, useMediaQuery } from "@mui/material";
import { useCheckoutCartInfo } from "../../../../hooks/useCheckoutCartInfo";
import { formatMoney, TABLET_SCREEN_SIZE } from "@brandclub/common-ui";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import { SubmitCheckoutButton } from "../../../components/SubmitCheckoutButton";

const Container = styled("div")({
  boxSizing: "border-box",
  display: "flex",
  borderRadius: "8px",
  flexDirection: "column",
  gap: "24px",
  width: "100%",
  backgroundColor: "#f6f8fa",
  maxWidth: 475,
  minWidth: 400,
  padding: "32px 32px 56px 32px",
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    gap: "16px",
    backgroundColor: "white",
    padding: "10px 0px 10px 0px",
    maxWidth: "100%",
    minWidth: "100%",
  },
});

const CartContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: "24px",
  width: "100%",
});

const CashBackSwatch = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  background: "#f5f7fa",
  borderRadius: 24,
  padding: "7px 14px",
  gap: 10,
  width: "fit-content",
  fontWeight: 600,
  fontSize: 14,
  color: theme.palette.primary.main,
  marginTop: "6px",
  boxShadow: "none",
}));

const FloatingOrderSummaryContainer = styled("div")({
  width: "100vw",
  boxSizing: "border-box",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  gap: "8px",
  position: "fixed",
  bottom: 0,
  left: 0,
  padding: "16px",
  backgroundColor: "white",
  boxShadow: "0px -4px 10px rgba(0, 0, 0, 0.1)",
  zIndex: 1000,
  display: "none",
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    display: "flex",
  },
});

const MobileOrderSummary = ({
  showSubmitButton,
  showOrderSummaryTitle = true,
}: {
  showSubmitButton: boolean;
  showOrderSummaryTitle?: boolean;
}) => {
  const isMobile = useMediaQuery(`(max-width: ${TABLET_SCREEN_SIZE}px)`);
  const { debug } = useAsyncCheckoutEnabled();
  const { items, isLoading, rewardAmounts } = useCheckoutCartInfo();

  if (isLoading) {
    return (
      <CartContainer>
        <Skeleton variant="rectangular" height={25} />
        <Skeleton variant="rectangular" height={25} />
        <Skeleton variant="rectangular" height={25} />
        <Skeleton variant="rectangular" height={25} />
      </CartContainer>
    );
  }

  if (!items?.length) {
    return null;
  }

  return (
    <Container>
      {showOrderSummaryTitle && (
        <div
          style={{
            width: "100%",
            marginBottom: "24.7px",
          }}
        >
          <SectionTitle>Order summary</SectionTitle>
        </div>
      )}
      <CartContainer>
        {items.map((item) => (
          // @ts-ignore
          <MobileOrderSummaryProduct key={item.stacklineSku} product={item} />
        ))}
      </CartContainer>

      {debug && <DebugPanel />}
      <RetailerErrorAndTerms />
      <div
        style={{
          width: "100%",
          marginTop: 12.4,
          marginBottom: 16,
          paddingBottom: showSubmitButton && isMobile ? 96 : 0,
        }}
      >
        <CartTotal />

        {showSubmitButton && (
          <div style={{ paddingTop: 16 }}>
            <SubmitCheckoutButton text="Place my order" />
            <BrandclubTermsAndConditions
              sx={{
                fontSize: 10,
                marginTop: "16px",
              }}
            />
          </div>
        )}
        {rewardAmounts.totalRewardAmount > 0 && (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap="16px"
            width="100%"
          >
            <CashBackSwatch>
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginRight: 0,
                }}
              >
                <AutoAwesomeOutlinedIcon fontSize={"small"} />
              </span>
              You'll earn {formatMoney(rewardAmounts.totalRewardAmount)} in cash
              back
            </CashBackSwatch>
          </Box>
        )}
      </div>
    </Container>
  );
};

export const FloatingOrderSummary = () => {
  return (
    <FloatingOrderSummaryContainer>
      <SubmitCheckoutButton showTotal text="Place my order" />
      <BrandclubTermsAndConditions
        sx={{
          fontSize: 10,
          marginTop: 0,
          marginBottom: 0,
        }}
      />
    </FloatingOrderSummaryContainer>
  );
};

export default MobileOrderSummary;
